import { useEffect, useState } from 'react';
import { ExternalLink, Pencil, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { jiraHtmlToReact } from '../../utils/jiraHtmlToReact';
import { normaliseJiraMarkup } from '../BugManagement/jiraMarkup';
import { splitTables } from './splitTables';
import './TestCaseDetail.css';
import './CreateTestCaseModal.css';
import { AUTOMATION_STATUS_OPTIONS, FIELD_OPTIONS, httpErrorMessage } from './testCaseConstants';
import FolderPicker from './FolderPicker';
import StepsEditor from './StepsEditor';
import LabelsPicker from './LabelsPicker';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';

// Jira test-case text arrives in one of two shapes: HTML fragments (rendered fields) or
// plain Markdown / Jira wiki markup (what is stored in the DB, e.g. "h2. Title",
// "* item", "{{code}}"). Anything containing an HTML tag goes through the sanitised HTML
// path; everything else is converted from wiki markup and rendered as Markdown.
const HTML_TAG_RE = /<\/?[a-z][a-z0-9-]*(\s[^>]*)?\/?>/i;

const MARKDOWN_COMPONENTS = {
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
};
// Table cells: keep inline formatting but drop the wrapping <p>.
const MARKDOWN_COMPONENTS_INLINE = {
  ...MARKDOWN_COMPONENTS,
  p: ({ children }) => <>{children}</>,
};

function RichText({ html, className = 'tcd-rich-text', emptyFallback = '—' }) {
  if (html == null || String(html).trim() === '') {
    return <span className="tcd-rich-empty">{emptyFallback}</span>;
  }
  const text = String(html);

  if (HTML_TAG_RE.test(text)) {
    const rendered = jiraHtmlToReact(text);
    if (!rendered) return <span className="tcd-rich-empty">{emptyFallback}</span>;
    return <div className={className}>{rendered}</div>;
  }

  // react-markdown escapes raw HTML by default.
  const normalised = normaliseJiraMarkup(text, { adjacentHashLists: true });
  return (
    <div className={`${className} tcd-rich-text-md`}>
      {splitTables(normalised).map((segment, i) =>
        segment.type === 'table' ? (
          <table key={i} className="tcd-md-table">
            <tbody>
              {segment.rows.map((row, r) => (
                <tr key={r}>
                  {row.cells.map((cell, c) => {
                    const Cell = row.header ? 'th' : 'td';
                    return (
                      <Cell key={c}>
                        <ReactMarkdown components={MARKDOWN_COMPONENTS_INLINE}>{cell}</ReactMarkdown>
                      </Cell>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : segment.text.trim() ? (
          <ReactMarkdown key={i} components={MARKDOWN_COMPONENTS}>{segment.text}</ReactMarkdown>
        ) : null
      )}
    </div>
  );
}

const JIRA_BASE = 'https://contentsquare.atlassian.net/browse';

function statusClass(status) {
  if (!status) return 'tcd-status-default';
  const s = status.toLowerCase();
  if (s === 'verified') return 'tcd-status-verified';
  if (s === 'backlog')  return 'tcd-status-backlog';
  return 'tcd-status-default';
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function SideField({ label, children }) {
  return (
    <div className="tcd-side-field">
      <span className="tcd-side-label">{label}</span>
      <span className="tcd-side-value">{children != null && children !== '' ? children : '—'}</span>
    </div>
  );
}

const PROJECT_TYPE_OPTIONS = ['Web', 'Mobile'];

function initialFormData(detail) {
  return {
    summary: detail?.summary ?? '',
    description: detail?.description ?? '',
    precondition: detail?.precondition ?? '',
    automationStatus: detail?.automationStatus ?? '',
    testPyramid: detail?.testPyramid ?? '',
    type: detail?.type ?? '',
    priority: detail?.priority ?? '',
    unit: detail?.unit ?? '',
    labels: (detail?.labels ?? []).map(l => l.label ?? l),
    projectType: (detail?.projectType ?? []).map(pt => pt.type ?? pt),
    techStack: (detail?.techStack ?? []).map(ts => ts.techStack ?? ts).join(', '),
  };
}

export default function TestCaseDetailModal({ isOpen, onClose, loading, error, detail, onSave, onDelete, folderTree }) {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(() => initialFormData(detail));
  const [folderPath, setFolderPath] = useState('');
  const [newFolder, setNewFolder] = useState(null); // { parentFolderId, name } | null
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [availableLabels, setAvailableLabels] = useState([]);
  const fieldOptions = FIELD_OPTIONS;

  useEffect(() => {
    api.get(`${QUALITY_SERVICE_API_URL}/hub/test-cases/labels`)
      .then(res => setAvailableLabels(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setEditMode(false);
      setConfirmDelete(false);
      setSaveError(null);
      setDeleteError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (detail && !editMode) {
      setFormData(initialFormData(detail));
    }
  }, [detail]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') { if (confirmDelete) { setConfirmDelete(false); } else { onClose(); } } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose, confirmDelete]);

  if (!isOpen) return null;

  function handleFieldChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    if (!detail) return;
    setSaving(true);
    setSaveError(null);
    try {
      const dto = {
        summary: formData.summary || undefined,
        // Only send rich-text fields the user actually edited. The backend rewrites them as a
        // single plain ADF paragraph, so re-sending an untouched Jira-authored value would
        // destroy its formatting (headings, lists, tables, line breaks).
        description: formData.description !== (detail.description ?? '') ? formData.description : undefined,
        precondition: formData.precondition !== (detail.precondition ?? '') ? formData.precondition : undefined,
        automationStatus: formData.automationStatus,
        testPyramid: formData.testPyramid,
        type: formData.type,
        priority: formData.priority,
        unit: formData.unit,
        labels: formData.labels,
        projectType: formData.projectType,
        techStack: formData.techStack ? formData.techStack.split(',').map(s => s.trim()).filter(Boolean) : [],
        folderPath: effectiveFolderPath || undefined,
        steps: steps.map(s => ({ description: s.description.trim(), expectedResult: s.expectedResult.trim() || undefined })),
      };
      await onSave(detail.key, dto, effectiveNewFolder);
      setEditMode(false);
    } catch (err) {
      console.error('Save test case error:', err);
      setSaveError(httpErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function handleEditToggle() {
    setFormData(initialFormData(detail));
    setFolderPath('');
    setNewFolder(null);
    setSteps(
      [...(detail?.steps ?? [])]
        .sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0))
        .map(s => ({ description: s.description ?? '', expectedResult: s.expectedResult ?? '' }))
    );
    setSaveError(null);
    setEditMode(true);
  }

  function handleCancelEdit() {
    setEditMode(false);
    setNewFolder(null);
    setSaveError(null);
  }

  async function handleConfirmDelete() {
    if (!detail) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(detail.key);
      setConfirmDelete(false);
      onClose();
    } catch (err) {
      console.error('Delete test case error:', err);
      setDeleteError(httpErrorMessage(err));
      setDeleting(false);
    }
  }

  const effectiveNewFolder = newFolder?.name?.trim()
    ? { parentFolderId: newFolder.parentFolderId, name: newFolder.name.trim() }
    : null;
  const effectiveFolderPath = folderPath;

  return (
    <div className="tcd-overlay" onClick={onClose}>
      <div className="tcd-modal" onClick={e => e.stopPropagation()}>

        {/* ── Top bar ── */}
        <div className="tcd-topbar">
          <div className="tcd-topbar-left">
            {detail && (
              <a
                href={`${JIRA_BASE}/${detail.key}`}
                target="_blank"
                rel="noopener noreferrer"
                className="tcd-key-link"
              >
                <ExternalLink size={12} />
                {detail.key}
              </a>
            )}
            {editMode ? (
              <input
                className="tcd-edit-summary-input"
                value={formData.summary}
                onChange={e => handleFieldChange('summary', e.target.value)}
                placeholder="Summary"
              />
            ) : (
              <h2 className="tcd-topbar-title">
                {detail ? detail.summary : loading ? 'Loading…' : 'Test Case Detail'}
              </h2>
            )}
          </div>
          <div className="tcd-topbar-actions">
            {detail && !editMode && onSave && (
              <button className="tcd-action-btn tcd-edit-btn" onClick={handleEditToggle} title="Edit test case">
                <Pencil size={13} /> Edit
              </button>
            )}
            {detail && !editMode && onDelete && (
              <button className="tcd-action-btn tcd-delete-btn" onClick={() => setConfirmDelete(true)} title="Delete test case">
                <Trash2 size={13} /> Delete
              </button>
            )}
            {editMode && (
              <>
                <button className="tcd-action-btn tcd-save-btn" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button className="tcd-action-btn tcd-cancel-btn" onClick={handleCancelEdit} disabled={saving}>
                  Cancel
                </button>
              </>
            )}
            <button className="tcd-close-btn" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        {saveError && <p className="tcd-state tcd-error">{saveError}</p>}

        {/* ── Shell ── */}
        {loading && <p className="tcd-state">Loading…</p>}
        {error   && <p className="tcd-state tcd-error">{error}</p>}

        {detail && !loading && (
          <div className="tcd-layout">

            {/* ── Main column ── */}
            <div className="tcd-main">
              {editMode ? (
                <>
                  <section className="tcd-section">
                    <p className="tcd-section-title">Description</p>
                    <textarea
                      className="tcd-edit-textarea"
                      value={formData.description}
                      onChange={e => handleFieldChange('description', e.target.value)}
                      placeholder="Description (optional)"
                      rows={4}
                    />
                  </section>
                  <section className="tcd-section">
                    <p className="tcd-section-title">Precondition</p>
                    <textarea
                      className="tcd-edit-textarea"
                      value={formData.precondition}
                      onChange={e => handleFieldChange('precondition', e.target.value)}
                      placeholder="Precondition (optional)"
                      rows={4}
                    />
                  </section>
                  <section className="tcd-section">
                    <p className="tcd-section-title">Test Steps</p>
                    <StepsEditor steps={steps} onChange={setSteps} />
                  </section>
                </>
              ) : (
                <>
                  {/* Description */}
                  <section className="tcd-section">
                    <p className="tcd-section-title">Description</p>
                    {detail.description
                      ? <RichText html={detail.description} className="tcd-rich-text tcd-body-text" />
                      : <p className="tcd-empty">No description provided.</p>
                    }
                  </section>

                  {/* Precondition */}
                  <section className="tcd-section">
                    <p className="tcd-section-title">Precondition</p>
                    {detail.precondition?.trim()
                      ? <RichText html={detail.precondition.trim()} className="tcd-rich-text tcd-precondition" />
                      : <p className="tcd-empty">No precondition provided.</p>
                    }
                  </section>

                  {/* Steps */}
                  {detail.steps && detail.steps.length > 0 && (
                    <section className="tcd-section">
                      <p className="tcd-section-title">Test Steps ({detail.steps.length})</p>
                      <table className="tcd-steps-table">
                        <thead>
                          <tr>
                            <th className="tcd-step-num">#</th>
                            <th>Description</th>
                            <th>Expected Result</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...detail.steps].sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0)).map((step, i) => (
                            <tr key={step.id ?? i}>
                              <td className="tcd-step-num">{i + 1}</td>
                              <td><RichText html={step.description} /></td>
                              <td><RichText html={step.expectedResult} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
                  )}
                </>
              )}
            </div>

            {/* ── Side panel ── */}
            <aside className="tcd-sidebar">
              {editMode ? (
                <>
                  <div className="tcd-side-field">
                    <span className="tcd-side-label">Folder</span>
                    <FolderPicker
                      rootChildren={folderTree?.children ?? []}
                      onPathChange={setFolderPath}
                      onNewFolderChange={setNewFolder}
                      allowCreate
                      initialFolderId={detail?.folderId}
                    />
                    {effectiveFolderPath && (
                      <span className="ctc-folder-path-preview" style={{ marginTop: 4 }}>
                        {detail?.project}/{effectiveFolderPath}
                        {effectiveNewFolder && <span className="ctc-folder-new-tag"> (new)</span>}
                      </span>
                    )}
                  </div>
                  <div className="tcd-side-field">
                    <span className="tcd-side-label">Automation Status</span>
                    <select
                      className="tcd-edit-select"
                      value={formData.automationStatus}
                      onChange={e => handleFieldChange('automationStatus', e.target.value)}
                    >
                      {AUTOMATION_STATUS_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt || '— none —'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="tcd-side-field">
                    <span className="tcd-side-label">Test Pyramid</span>
                    <select className="tcd-edit-select" value={formData.testPyramid} onChange={e => handleFieldChange('testPyramid', e.target.value)}>
                      <option value="">— none —</option>
                      {(fieldOptions.testPyramid ?? []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="tcd-side-field">
                    <span className="tcd-side-label">Priority</span>
                    <select className="tcd-edit-select" value={formData.priority} onChange={e => handleFieldChange('priority', e.target.value)}>
                      <option value="">— none —</option>
                      {(fieldOptions.priority ?? []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="tcd-side-field">
                    <span className="tcd-side-label">Type</span>
                    <select className="tcd-edit-select" value={formData.type} onChange={e => handleFieldChange('type', e.target.value)}>
                      <option value="">— none —</option>
                      {(fieldOptions.type ?? []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="tcd-side-field">
                    <span className="tcd-side-label">Unit</span>
                    <select className="tcd-edit-select" value={formData.unit} onChange={e => handleFieldChange('unit', e.target.value)}>
                      <option value="">— none —</option>
                      {(fieldOptions.unit ?? []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="tcd-side-field">
                    <span className="tcd-side-label">Project Type <span className="ctc-required">*</span></span>
                    <LabelsPicker
                      selectedLabels={formData.projectType}
                      onChange={v => handleFieldChange('projectType', v)}
                      availableLabels={PROJECT_TYPE_OPTIONS}
                    />
                  </div>
                  <div className="tcd-side-field">
                    <span className="tcd-side-label">Tech Stack <span className="ctc-required">*</span></span>
                    <input className="tcd-edit-input" value={formData.techStack} onChange={e => handleFieldChange('techStack', e.target.value)} placeholder="Comma-separated, e.g. QT, UX" />
                  </div>
                  <div className="tcd-side-field">
                    <span className="tcd-side-label">Labels</span>
                    <LabelsPicker
                      selectedLabels={formData.labels}
                      onChange={labels => handleFieldChange('labels', labels)}
                      availableLabels={availableLabels}
                    />
                  </div>
                </>
              ) : (
                <>
                  <SideField label="Status">
                    <span className={`tcd-status-chip ${statusClass(detail.status)}`}>
                      {detail.status || '—'}
                    </span>
                  </SideField>
                  <SideField label="Folder">{detail.folder?.name}</SideField>
                  <SideField label="Automation Status">{detail.automationStatus}</SideField>
                  <SideField label="Test Pyramid">{detail.testPyramid}</SideField>
                  <SideField label="Priority">{detail.priority}</SideField>
                  <SideField label="Type">{detail.type}</SideField>
                  <SideField label="Project Type">{(detail.projectType ?? []).map(pt => pt.type ?? pt).join(', ')}</SideField>
                  <SideField label="Tech Stack">{(detail.techStack ?? []).map(ts => ts.techStack ?? ts).join(', ')}</SideField>
                  <SideField label="Unit">{detail.unit}</SideField>
                  <SideField label="Product Component">{detail.techTeam}</SideField>
                  <SideField label="Technical Component">{detail.techComponent}</SideField>
                  <div className="tcd-side-divider" />
                  <SideField label="Reporter">{detail.reporter}</SideField>
                  <SideField label="Assignee">{detail.assignee}</SideField>
                  <SideField label="Creator">{detail.creator}</SideField>
                  <SideField label="Automated By">{detail.automatedBy}</SideField>
                  <div className="tcd-side-divider" />
                  <SideField label="Created">{formatDate(detail.created)}</SideField>
                </>
              )}
            </aside>

          </div>
        )}

        {/* ── Delete confirmation overlay ── */}
        {confirmDelete && (
          <div className="tcd-confirm-overlay" onClick={() => setConfirmDelete(false)}>
            <div className="tcd-confirm-dialog" onClick={e => e.stopPropagation()}>
              <p className="tcd-confirm-title">Delete test case?</p>
              <p className="tcd-confirm-body">
                <strong>{detail?.key}</strong> — {detail?.summary}
                <br />
                This will permanently delete the test case from Jira.
              </p>
              {deleteError && <p className="tcd-state tcd-error tcd-confirm-error">{deleteError}</p>}
              <div className="tcd-confirm-actions">
                <button
                  className="tcd-action-btn tcd-delete-btn"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting…' : 'Yes, delete'}
                </button>
                <button
                  className="tcd-action-btn tcd-cancel-btn"
                  onClick={() => { setConfirmDelete(false); setDeleteError(null); }}
                  disabled={deleting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

